import { useCallback, useMemo } from "react";

import type { TextFieldProps } from "@mui/material";
import { MenuItem, TextField } from "@mui/material";
import countries from "i18n-iso-countries";
import enCountryLocale from "i18n-iso-countries/langs/en.json";
import esCountryLocale from "i18n-iso-countries/langs/es.json";
import { useTranslation } from "react-i18next";

export type CountryCodeKey = keyof typeof enCountryLocale.countries;

export type CountryCodeSelectProps = {
  value: CountryCodeKey | null;
  onChange: (value: CountryCodeKey | null) => void;
  label?: string;
  name?: string;
  required?: boolean;
} & Omit<TextFieldProps, "value" | "onChange" | "select" | "label" | "name">;

let countriesRegistered = false;

const ensureCountriesRegistered = () => {
  if (countriesRegistered) return;
  countries.registerLocale(enCountryLocale);
  countries.registerLocale(esCountryLocale);
  countriesRegistered = true;
};

export const CountryCodeSelect = ({
  value,
  onChange,
  label,
  name,
  required = false,
  ...textFieldProps
}: CountryCodeSelectProps) => {
  const { i18n } = useTranslation();

  const options = useMemo(() => {
    ensureCountriesRegistered();
    const language = i18n.language.split("-")[0];
    let names: Record<string, string>;

    try {
      names = countries.getNames(language, { select: "official" });
    } catch {
      names = countries.getNames("en", { select: "official" });
    }

    return Object.entries(names)
      .map(([key, displayName]) => ({ key: key as CountryCodeKey, displayName }))
      .sort((left, right) => left.displayName.localeCompare(right.displayName));
  }, [i18n.language]);

  const handleChange = useCallback<NonNullable<TextFieldProps["onChange"]>>(
    (event) => {
      const nextValue = event.target.value as CountryCodeKey;
      onChange(nextValue ? nextValue : null);
    },
    [onChange],
  );

  return (
    <TextField
      select
      name={name}
      label={label}
      required={required}
      value={value ?? ""}
      onChange={handleChange}
      {...textFieldProps}
    >
      {options.map((option) => (
        <MenuItem key={option.key} value={option.key}>
          {option.displayName}
        </MenuItem>
      ))}
    </TextField>
  );
};
